import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { isOptimisticAddressId } from '@/api/addresses/isOptimisticAddressId'
import { useGetPaymentLink } from '@/api/payments/mutations/useGetPaymentLink'
import { ID_REQUIRED_THRESHOLD, TAX_FREE_THRESHOLD } from '@/api/payments/paymentsService'
import { CheckCouponResponseData } from '@/api/payments/types/CheckCouponResponse'
import { CreatePaymentLinkPayload } from '@/api/payments/types/CreatePaymentLinkPayload'
import { PaymentSummaryResponse } from '@/api/payments/types/PaymentSummaryResponse'
import { UserPkg } from '@/api/user-package/types/UserPkg'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import AttentionIcon from '@/components/icons/attention.svg?react'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'
import { isValidIsraeliId } from '@/helpers/israeliId'
import { RoutePath } from '@/RoutePath'

import { PkgPreview } from '../ClientPkg/components/PkgPreview'
import { CouponInput } from './components/CouponInput'
import { CreditToUseSection } from './components/CreditToUseSection'
import { PaymentData } from './components/PaymentData'
import { PaymentSummaryDetails } from './components/PaymentSummaryDetails'
import { ShippingOptions } from './ShippingPage'
interface Props {
  pkg: UserPkg
  shippingOptions: ShippingOptions
  paymentData: PaymentData
  onUpdatePaymentData: (data: Partial<PaymentData>) => void
  pkgValue: number
  summaryData: PaymentSummaryResponse | undefined
  isLoadingSummary: boolean
  onCompletePaymentSuccess: () => void
}

export default function SummaryPage({
  pkg,
  shippingOptions,
  paymentData,
  onUpdatePaymentData,
  pkgValue,
  summaryData,
  isLoadingSummary,
  onCompletePaymentSuccess,
}: Props) {
  const { t } = useTranslation()
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()
  const { isPending: isLoadingPaymentLink, mutateAsync: getPaymentLink } = useGetPaymentLink()

  const israeliIdRef = useRef<HTMLInputElement>(null)

  const shouldCollectIsraeliId = pkgValue > ID_REQUIRED_THRESHOLD
  const couponDiscountAmount = summaryData?.couponDiscountUSD ?? 0

  const creditBalanceUSD = summaryData?.creditBalance ?? 0
  const effectiveCreditToUseUSD = summaryData?.creditToUseUSD ?? paymentData.creditToUseUSD ?? 0
  const maxCreditToUse = Math.min(creditBalanceUSD, (summaryData?.finalPriceUSD ?? 0) + effectiveCreditToUseUSD)

  const handlePayment = async () => {
    if (shouldCollectIsraeliId && !isValidIsraeliId(paymentData.israeliId)) {
      setErrorMessage(t('payment.invalidId'))
      setShowErrorModal(true)
      israeliIdRef.current?.focus()
      return
    }

    if (!shippingOptions.selectedAddressId || isOptimisticAddressId(shippingOptions.selectedAddressId)) {
      setErrorMessage(t('payment.serverError'))
      setShowErrorModal(true)
      return
    }

    try {
      const payload: CreatePaymentLinkPayload = {
        pkgId: pkg.id,
        shippingAddressId: shippingOptions.selectedAddressId,
        couponCode: paymentData.couponCode || undefined,
        isInsuranceAdded: shippingOptions.isInsuranceAdded,
        costumerIsraeliId: paymentData.israeliId,
      }

      if (effectiveCreditToUseUSD > 0) {
        payload.creditToUseUSD = effectiveCreditToUseUSD
      }

      const data = await getPaymentLink(payload)
      if (data.success) {
        onCompletePaymentSuccess()
        return
      }
      onUpdatePaymentData({ paymentLinkData: data ?? null })
      navigate(`${RoutePath.PaymentProcess}/${pkg.id}`)
    } catch (error) {
      console.error({ message: 'Get payment link error', error })
      setErrorMessage(t('payment.serverError'))
      setShowErrorModal(true)
    }
  }

  const handleCouponApply = (couponData: CheckCouponResponseData) => {
    onUpdatePaymentData({
      coupon: couponData.coupon,
      feesDiscount: couponData.feesDiscount,
      couponCode: couponData.coupon.code,
    })
  }

  const isContinueDisabled = useMemo(() => {
    return isLoadingPaymentLink || isLoadingSummary || !shippingOptions.selectedShippingMethod
  }, [isLoadingPaymentLink, isLoadingSummary, shippingOptions.selectedShippingMethod])

  return (
    <div className='space-y-6'>
      {/* Title Section */}
      <div>
        <h1 className='text-text-primary mb-2 text-2xl font-medium'>{t('payment.summaryAndConfirm')}</h1>
        <p className='text-text-secondary text-sm leading-relaxed'>{t('payment.summarySubtitle')}</p>
      </div>

      {/* Package Card */}
      <PkgPreview pkg={pkg} currentView='all' hideActions hideStatus />
      <PaymentSummaryDetails
        couponDiscountAmount={couponDiscountAmount}
        creditToUseUSD={effectiveCreditToUseUSD}
        summaryData={summaryData}
        paymentData={paymentData}
        isLoadingSummary={isLoadingSummary}
        totalPrice={summaryData?.finalPriceUSD ?? 0}
      />

      {creditBalanceUSD > 0 && (
        <CreditToUseSection
          creditBalanceUSD={creditBalanceUSD}
          creditToUseUSD={effectiveCreditToUseUSD}
          maxCreditToUse={maxCreditToUse}
          setCreditToUseUSD={value => onUpdatePaymentData({ creditToUseUSD: value })}
        />
      )}

      {shouldCollectIsraeliId && (
        <div className='border-border-default space-y-2 rounded-lg border bg-white p-3'>
          <h3 className='text-text-primary text-sm font-medium'>
            {t('payment.packageValueAboveId', { threshold: ID_REQUIRED_THRESHOLD })}
          </h3>
          <TextField
            ref={israeliIdRef}
            placeholder={t('payment.idPlaceholder')}
            inputMode='numeric'
            value={paymentData.israeliId}
            onChange={e => onUpdatePaymentData({ israeliId: e.target.value })}
            className='w-full'
          />
        </div>
      )}
      {/* Taxes Info */}
      {pkgValue > 0 && (
        <div className='rounded-lg bg-blue-50 p-4'>
          {pkgValue > TAX_FREE_THRESHOLD ? (
            <div className='text-sm'>{t('payment.taxTip')}</div>
          ) : (
            <div className='text-sm'>{t('payment.taxFreeMessage', { threshold: TAX_FREE_THRESHOLD })}</div>
          )}
        </div>
      )}
      {/* Coupon Input */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.couponCodeLabel')}</h3>
        <CouponInput
          pkgId={pkg.id}
          coupon={paymentData.coupon}
          onApplyCoupon={handleCouponApply}
          onError={msg => {
            setErrorMessage(msg)
            setShowErrorModal(true)
          }}
        />
      </div>

      {/* Payment Button */}
      <div className='flex justify-center'>
        <Button
          loading={isLoadingPaymentLink}
          onClick={handlePayment}
          disabled={isContinueDisabled}
          className='w-full max-w-[335px]'
          size='lg'
        >
          {t('payment.continueToPaymentProcess')}
        </Button>
      </div>

      {/* Error Modal */}
      <ConfirmDialog
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        confirmText={t('common.close')}
        Icon={AttentionIcon}
        title={t('payment.attention')}
        content={errorMessage}
      />
    </div>
  )
}
